import { TenantMemberDisplayData } from '../identity'
import { Audience, PaginatedSearch, SerializedSort, WithPermissions } from '../shared/types'

export type PointsPeriod = string

export enum AccountTypeEnum {
  REDEEMABLE = 'redeemable',
  ALLOCATION = 'allocation',
  FUND = 'fund',
  PAYOUT = 'payout',
}

export type Account = WithPermissions<{
  id: string
  ownerId: string
  tenantId: string
  balance: number
  initialBalance: number
  allTimeReceived?: number
  type: AccountTypeEnum
  lockedAt: string | null
  createdAt: string
  updatedAt: string
  period: PointsPeriod | null
  // embeds
  memberDisplayData?: TenantMemberDisplayData
  __entity_type__: 'account'
}>

// For more information on points/periods, see the Syndicate repo here:
// https://github.com/KudosInc/syndicate/blob/main/project/src/apps/points/periods.ts
export enum PointsPeriodType {
  NEVER = 'never',
  MONTH = 'month',
  YEAR = 'year',
  QUARTER = 'quarter',
}

export interface BudgetSetting {
  id: string
  tenantId: string
  allocationPeriodType: PointsPeriodType
  redeemablePeriodType: PointsPeriodType | null
  defaultCanReceivePoints: boolean
  nextAllocationAt: string
  nextExpiryAt: string
  createdAt: string
  updatedAt: string
  __entity_type__: 'budgetSetting'
}

export type BudgetSettingParams = Pick<
  BudgetSetting,
  'allocationPeriodType' | 'redeemablePeriodType' | 'defaultCanReceivePoints'
>

export type BudgetSettingEmbeds = 'nextAllocationAt' | 'nextExpiryAt'

export type GetBudgetSettingsParams = {
  embeds?: BudgetSettingEmbeds[]
}

export type BudgetItemAllocationConfig = {
  points: number
  pointsPerReport?: number
}

export type OnboardingConfig = {
  points: number
  days: number
}

export type BudgetItemOnboardingConfig = {
  milestones: OnboardingConfig[]
}

export type AnniversaryConfig = {
  points: number
  year: number
}

export type BudgetItemAnniversaryConfig = {
  points: number
  overrides: AnniversaryConfig[]
}

export enum BudgetItemType {
  ALLOCATION = 'allocation',
  BIRTHDAY = 'birthday',
  ONBOARDING = 'onboarding',
  WELCOME = 'welcome',
  ANNIVERSARY = 'anniversary',
  POINTS_BANK = 'pointsBank',
}

export type BudgetItemConfig =
  | BudgetItemAllocationConfig
  | BudgetItemOnboardingConfig
  | BudgetItemAnniversaryConfig

export type CreateBudgetItemParams = {
  name: string
  config: BudgetItemConfig
  teamIds?: string[]
  itemType: BudgetItemType
  tenantWide?: boolean
  description?: string
  ownerIds?: string[]
}

export type EstimateBudgetItemParams = CreateBudgetItemParams & {
  idIn?: string[]
}

export type ListBudgetItemOwnersParams = {
  search?: string
}

export type ListBudgetItemOwnersResults = {
  tenantMembers: TenantMemberDisplayData[]
}

export type BudgetItem = WithPermissions<{
  id: string
  name: string
  description: string
  tenantId: string
  itemType: BudgetItemType
  teamIds: string[]
  tenantWide: boolean
  config: BudgetItemConfig
  createdAt: string
  updatedAt: string

  //embeds
  estimate?: BudgetItemEstimate
  balance?: PointsBankBalance
  audience?: Audience
  owners?: TenantMemberDisplayData[]
  __entity_type__: 'budgetItem'
}>

export type BudgetItemsEmbeds = 'estimate' | 'balance' | 'audience' | 'owners'

export type ListBudgetItemsParams = PaginatedSearch<
  {
    idIn?: string[]
    itemTypeIn?: BudgetItemType[]
  },
  BudgetItemsEmbeds
>

export type BudgetItemEstimate = {
  periodType?: PointsPeriodType
  numberAffected: number
  yearlySpend: number
}

export type PointBalanceWarning = 'nearLimit' | 'limitReached'

export type PointsBankBalance = {
  available: number
  spent: number
  limit: number
  warnings: PointBalanceWarning[]
  name: string
}

export type AccountEmbeds = 'allTimeReceived'

export type ListAccountsParams = {
  embeds?: AccountEmbeds[]
}

export type TransactionEmbeds =
  | 'sender'
  | 'recipient'
  | 'expirationDate'
  | 'periodName'
  | 'reversal'
  | 'initiatedBy'
  | 'senderName'
  | 'recipientName'
  | 'budgetItemName'

export type ListTransactionParams = {
  recipientIdIn?: string[]
  senderIdIn?: string[]
  transactionTypeIn?: string[]
  createdInTimeSpan?: string[]
  teamIdIn?: string[]
  ownerIdIn?: string[]
  budgetItemIdIn?: string[]
  dateRangePreset?: string
}

export type ListTransactionRecipientsParams = PaginatedSearch<
  ListTransactionParams,
  TransactionEmbeds
>

export type ListTransactionRecipientsCursorParams = ListTransactionParams & {
  embeds?: TransactionEmbeds[]
  sorting?: string[]
  cursor?: string | null
  pageSize?: number
}

export enum TransactionTypeEnum {
  EXPIRE_REDEEMABLE = 'expire_redeemable',
  EXPIRE_ALLOCATION = 'expire_allocation',
  ALLOCATION = 'allocation',
  REDEEM = 'redeem',
  REFUND = 'refund',
  RECOGNITION = 'recognition',
  ENDORSEMENT = 'endorsement',
  AWARD = 'award',
  WELCOME = 'welcome',
  BIRTHDAY = 'birthday',
  ANNIVERSARY = 'anniversary',
  ONBOARDING = 'onboarding',
  MIGRATED_REDEEMABLE = 'migrated_redeemable', // Migrated all time redeemable amount
  REDEEMABLE_ADD = 'redeemable_add',
  REDEEMABLE_REMOVE = 'redeemable_remove',
  ALLOCATION_ADD = 'allocation_add',
  ALLOCATION_REMOVE = 'allocation_remove',
  REVERSAL = 'reversal',
  INCENTIVE = 'incentive',
  DRAIN_REDEEMABLE = 'drain_redeemable',
  DRAIN_ALLOCATION = 'drain_allocation',
}

export type TransactionInitiator = {
  id: string
  // `null` signals the initiating admin has since been deleted. The BE keeps
  // the id (so the FK is still valid) but stops returning their display name.
  name: string | null
}

export type TransactionRecipient = WithPermissions<{
  senderId: string
  recipientId: string
  senderPeriod: PointsPeriod
  recipientPeriod: PointsPeriod
  transactionType: TransactionTypeEnum
  createdAt: string
  amount: number
  expirationDate?: string
  periodName?: string
  notes: string | null
  transactionId: string
  recipientAccountId: string
  budgetItemId: string | null
  // embeds
  senderName?: string
  recipientName?: string
  initiatedByName?: string
  reversal?: {
    createdAt: string
    reason: string
  }
  // `null` for system-initiated transactions (e.g. scheduled allocations) and
  // legacy rows with no recorded actor; an object when an admin/user triggered
  // the transaction. Keep `?` for embed-not-loaded responses.
  initiatedBy?: TransactionInitiator | null
  budgetItemName?: string
}>

export type RedeemablePointsParams = {
  teamIdIn?: string[]
  memberIdIn?: string[]
  includeLocked?: boolean
}

export type TotalRedeemablePoints = {
  total: number
}

export type RedeemableAccountsEmbeds = 'members'

export type RedeemableAccountsParams = PaginatedSearch<
  RedeemablePointsParams,
  RedeemableAccountsEmbeds
>

export type ReverseTransactionParams = {
  reason: string
}

export type AddRemovePointsParams = {
  amount: number
  reason: string
}

export type Transaction = {
  id: string
  tenantId: string
  createdAt: string
  amount: number
  type: TransactionTypeEnum
  deduplicationId: string | null
  initiatedBy: string
  budgetItemId: string | null
  notes: string
  __entity_type__: 'transaction'
}

export type AllocationForecast = {
  itemType: BudgetItemType
  name: string
  amount: number
  on: string
}

export type BudgetItemUsageDetails = {
  id: string
  name: string
  entityType: 'awardType' | 'incentive'
}

export type BudgetItemUsage = {
  awards: BudgetItemUsageDetails[]
  incentives: BudgetItemUsageDetails[]
}

export type BudgetItemSortOption = 'createdAt' | 'name'

export type AllocationForecastParams = {
  sorting?: SerializedSort<BudgetItemSortOption>[]
}
